/**
 * SeriesCreateModal — création rapide d'une série depuis le dropdown
 * « Série » de l'éditeur d'œuvre.
 *
 * Le caller exécute la mutation (`useCreateSeries`) et reçoit la série
 * créée via `onCreated` pour auto-sélectionner dans le form parent.
 *
 * Backend : POST /api/series est idempotent par slug — saisir le nom
 * d'une série existante renvoie la row existante (pas d'erreur).
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'

import Button from '@/components/ui/Button'
import Modal from '@/components/ui/Modal'
import FormField from '@/components/forms/FormField'
import FormTextarea from '@/components/forms/FormTextarea'
import type { SeriesResponse } from '@/types/works'

const schema = z.object({
  name_fr: z.string().trim().min(1, 'Nom requis').max(160, 'Nom trop long (160 max)'),
  description_fr: z.string().trim().max(2000, 'Description trop longue').optional(),
})

type FormValues = z.infer<typeof schema>

interface SeriesCreateModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: (series: SeriesResponse) => void
  onSubmit: (payload: { name_fr: string; description_fr?: string }) => Promise<SeriesResponse>
  isSubmitting?: boolean
}

export default function SeriesCreateModal({
  isOpen,
  onClose,
  onCreated,
  onSubmit,
  isSubmitting = false,
}: SeriesCreateModalProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name_fr: '', description_fr: '' },
  })

  const handleSubmit = form.handleSubmit(async (values) => {
    const payload = {
      name_fr: values.name_fr.trim(),
      description_fr: values.description_fr?.trim() || undefined,
    }
    const created = await onSubmit(payload)
    form.reset()
    onCreated(created)
  })

  const handleClose = () => {
    if (isSubmitting) return
    form.reset()
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      kicker="Atelier · Nouvelle série"
      title="Créer une série"
      size="form"
    >
      <FormProvider {...form}>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <p className="ui-body">
              Une série regroupe plusieurs œuvres (saison, collection, cycle thématique). Le nom
              anglais sera produit automatiquement par Claude Vision lors de la prochaine
              extraction.
            </p>
            <FormField
              name="name_fr"
              label="Nom"
              required
              placeholder="« Brume », « Cycle d'été »…"
              focusOnMount
            />
            <FormTextarea
              name="description_fr"
              label="Description"
              helper="Optionnel — note interne pour situer la série."
              rows={3}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="neutral" onClick={handleClose} disabled={isSubmitting}>
              Annuler
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Création…' : 'Créer la série'}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  )
}
